import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from "../app-routing.module";
import { ReactiveFormsModule } from "@angular/forms";

import { CalendarModule } from "primeng/calendar";
import { TableModule } from "primeng/table";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { TooltipModule } from "primeng/tooltip";
import { SlideMenuModule } from "primeng/slidemenu";
import { DropdownModule } from "primeng/dropdown";
import { ScrollPanelModule } from "primeng/scrollpanel";
import { SelectButtonModule } from "primeng/selectbutton";
import { RadioButtonModule } from "primeng/radiobutton";
import { CheckboxModule } from "primeng/checkbox";
import { ToastModule } from "primeng/toast";
import { TabViewModule } from "primeng/tabview";
import { MessageModule } from "primeng/message";
import { MessagesModule } from "primeng/messages";
import { ButtonModule } from "primeng/button";
import { ProgressBarModule } from "primeng/progressbar";
import { FileUploadModule } from "primeng/fileupload";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { ChipsModule } from "primeng/chips";
import { PanelModule } from "primeng/panel";
import { InputSwitchModule } from "primeng/inputswitch";
import { AutoCompleteModule } from "primeng/autocomplete";

import { DisableControlDirective } from "./directives/disable-control.directive";
import { HeaderComponent } from "./components/header/header.component";
import { SeacherComponent } from "./components/seacher/seacher.component";
import { SearchHistoricalComponent } from "./components/search-historical/search-historical.component";
import { UserButtonComponent } from "./components/user-button/user-button.component";
import { HistoricalTableComponent } from "./components/historical-table/historical-table.component";
import { ReviewResultComponent } from "./components/review-result/review-result.component";
import { FindingContentComponent } from "./components/finding-content/finding-content.component";
import { FindingCrudComponent } from "./components/finding-crud/finding-crud.component";
import { AfsReviewTableComponent } from "./components/afs-review-table/afs-review-table.component";
import { ContentBorderInfoComponent } from "./components/content-border-info/content-border-info.component";
import { SumPipe } from "./pipes/sum.pipe";
import { MinsecPipe } from "./pipes/minsec.pipe";
import { StrongFindingsPipe } from "./pipes/strongFindings.pipe";
import { MenuRoleComponent } from "./components/menu-role/menu-role.component";
import { MenuDashboardComponent } from "./components/menu-dashboard/menu-dashboard.component";
import { UsersRoleTableComponent } from "./components/users-role-table/users-role-table.component";
import { TestDictionaryTableComponent } from "./components/test-dictionary-table/test-dictionary-table.component";
import { PanelComponent } from "./components/panel/panel.component";
import { TestDescriptionsTableComponent } from "./components/test-descriptions-table/test-descriptions-table.component";
import { ToolsTableComponent } from "./components/tools-table/tools-table.component";
import { AreaChipsComponent } from "./components/area-chips/area-chips.component";
import { AddedFindingTableComponent } from "./components/added-finding-table/added-finding-table.component";
import { RemovedFindingTableComponent } from "./components/removed-finding-table/removed-finding-table.component";

import { EzshareTableComponent } from "./components/ezshare-table/ezshare-table.component";
import { ModalUsersComponent } from "./components/modal-users/modal-users.component";
import { UpdateActionPlanTableComponent } from "./components/update-action-plan-table/update-action-plan-table.component";
import { ActionPlanTableComponent } from "./components/action-plan-table/action-plan-table.component";
import { ModalFindingsComponent } from "./components/modal-findings/modal-findings.component";
import { ModalInternalReviewComponent } from "./components/modal-internal-review/modal-internal-review.component";
import { ModalICRRoleComponent } from "./components/modal-icr-role/modal-icr-role.component";

import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatTableModule } from "@angular/material/table";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule, MatRippleModule } from "@angular/material/core";
import { CdkTableModule } from "@angular/cdk/table";
import { CdkStepperModule } from "@angular/cdk/stepper";
import { A11yModule } from "@angular/cdk/a11y";
import { CdkTreeModule } from "@angular/cdk/tree";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatBadgeModule } from "@angular/material/badge";
import { MatBottomSheetModule } from "@angular/material/bottom-sheet";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatStepperModule } from "@angular/material/stepper";
import { MatChipsModule } from "@angular/material/chips";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSliderModule } from "@angular/material/slider";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatTreeModule } from "@angular/material/tree";
import { OverlayModule } from "@angular/cdk/overlay";
import { PortalModule } from "@angular/cdk/portal";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { DialogCommentComponent } from "./components/dialog-comment/dialog-comment.component";

@NgModule({
  declarations: [
    HeaderComponent,
    SeacherComponent,
    SearchHistoricalComponent,
    UserButtonComponent,
    HistoricalTableComponent,
    ReviewResultComponent,
    FindingContentComponent,
    FindingCrudComponent,
    AfsReviewTableComponent,
    UpdateActionPlanTableComponent,
    ActionPlanTableComponent,
    ContentBorderInfoComponent,
    DisableControlDirective,
    SumPipe,
    MinsecPipe,
    StrongFindingsPipe,
    MenuRoleComponent,
    MenuDashboardComponent,
    UsersRoleTableComponent,
    TestDictionaryTableComponent,
    PanelComponent,
    TestDescriptionsTableComponent,
    ToolsTableComponent,
    AreaChipsComponent,
    AddedFindingTableComponent,
    RemovedFindingTableComponent,
    EzshareTableComponent,
    ModalUsersComponent,
    ModalFindingsComponent,
    ModalInternalReviewComponent,
    ModalICRRoleComponent,
    DialogCommentComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MatButtonModule,
    MatTableModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CdkTableModule,
    A11yModule,
    CdkStepperModule,
    CdkTreeModule,
    DragDropModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatStepperModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    OverlayModule,
    PortalModule,
    ScrollingModule,
    CommonModule,
    FormsModule,
    CalendarModule,
    AppRoutingModule,
    TableModule,
    DialogModule,
    MultiSelectModule,
    InputTextModule,
    TooltipModule,
    SlideMenuModule,
    DropdownModule,
    ScrollPanelModule,
    SelectButtonModule,
    RadioButtonModule,
    CheckboxModule,
    ReactiveFormsModule,
    ToastModule,
    TabViewModule,
    MessageModule,
    MessagesModule,
    ButtonModule,
    ProgressBarModule,
    FileUploadModule,
    OverlayPanelModule,
    ChipsModule,
    PanelModule,
    InputSwitchModule,
    AutoCompleteModule,
  ],
  exports: [
    MatButtonModule,
    MatTableModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CdkTableModule,
    A11yModule,
    CdkStepperModule,
    CdkTreeModule,
    DragDropModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatStepperModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    OverlayModule,
    PortalModule,
    ScrollingModule,
    HeaderComponent,
    SeacherComponent,
    SearchHistoricalComponent,
    UserButtonComponent,
    HistoricalTableComponent,
    ReviewResultComponent,
    FindingContentComponent,
    FindingCrudComponent,
    AfsReviewTableComponent,
    UpdateActionPlanTableComponent,
    ActionPlanTableComponent,
    ContentBorderInfoComponent,
    DisableControlDirective,
    UsersRoleTableComponent,
    TestDictionaryTableComponent,
    PanelComponent,
    TestDescriptionsTableComponent,
    ToolsTableComponent,
    AreaChipsComponent,
    AddedFindingTableComponent,
    RemovedFindingTableComponent,
    CalendarModule,
    AppRoutingModule,
    TableModule,
    DialogModule,
    MultiSelectModule,
    InputTextModule,
    TooltipModule,
    SlideMenuModule,
    DropdownModule,
    ScrollPanelModule,
    SelectButtonModule,
    RadioButtonModule,
    CheckboxModule,
    ReactiveFormsModule,
    ToastModule,
    BrowserAnimationsModule,
    TabViewModule,
    MessageModule,
    MessagesModule,
    ButtonModule,
    ProgressBarModule,
    FileUploadModule,
    OverlayPanelModule,
    ChipsModule,
    PanelModule,
    InputSwitchModule,
    AutoCompleteModule,
    SumPipe,
    MinsecPipe,
    StrongFindingsPipe,
    EzshareTableComponent,
  ],
  bootstrap: [],
  providers: [],
  entryComponents: [
    ModalFindingsComponent,
    ModalInternalReviewComponent,
    ModalICRRoleComponent,
    DialogCommentComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
